import { StateSnapshotSchema, type StateSnapshot, type PresenceUpdate } from "@saboteur/contracts";
import { shouldReplaceSnapshot } from "@client/realtime/recovery";

type Audience = StateSnapshot["audience"];
type Snapshot<A extends Audience> = Extract<StateSnapshot, { audience: A }>;

/** One validated, monotonically versioned snapshot stream per audience/session. */
export class SnapshotChannel<A extends Audience> {
  readonly #listeners = new Set<(snapshot: Snapshot<A>) => void>();
  #snapshot: Snapshot<A> | undefined;
  #version: number;

  constructor(
    private readonly audience: A,
    initialVersion = -1,
  ) {
    this.#version = initialVersion;
  }

  get version(): number {
    return this.#version;
  }

  subscribe(listener: (snapshot: Snapshot<A>) => void): () => void {
    this.#listeners.add(listener);
    if (this.#snapshot) listener(this.#snapshot);
    return () => this.#listeners.delete(listener);
  }

  accept(raw: unknown, explicitSync = false): "accepted" | "ignored" | "gap" {
    const parsed = StateSnapshotSchema.safeParse(raw);
    if (!parsed.success || parsed.data.audience !== this.audience) return "ignored";
    const snapshot = parsed.data;
    const stateVersion =
      snapshot.audience === "player" ? snapshot.state.public.version : snapshot.state.version;
    if (snapshot.version !== stateVersion) return "ignored";
    if (!shouldReplaceSnapshot(this.#version, snapshot.version, explicitSync)) return "ignored";
    if (!explicitSync && this.#version >= 0 && snapshot.version > this.#version + 1) return "gap";
    // The runtime discriminant check above establishes the generic audience.
    this.#snapshot = snapshot as Snapshot<A>;
    this.#version = snapshot.version;
    for (const listener of this.#listeners) listener(this.#snapshot);
    return "accepted";
  }

  mergePresence(presence: PresenceUpdate): boolean {
    if (presence.version > this.#version) return false;
    const snapshot: StateSnapshot | undefined = this.#snapshot;
    if (!snapshot || presence.version !== this.#version) return true;
    const publicState = snapshot.audience === "player" ? snapshot.state.public : snapshot.state;
    const connected = new Map(
      presence.participants.map((player) => [player.participantId, player.connected]),
    );
    const participants = publicState.participants.map((player) => ({
      ...player,
      connected: connected.get(player.participantId) ?? player.connected,
    }));
    this.accept(
      snapshot.audience === "player"
        ? {
            ...snapshot,
            state: { ...snapshot.state, public: { ...snapshot.state.public, participants } },
          }
        : { ...snapshot, state: { ...snapshot.state, participants } },
      true,
    );
    return true;
  }
}

export class ObservableStatus<T> {
  readonly #listeners = new Set<(status: T) => void>();
  constructor(private value: T) {}
  set(value: T): void {
    if (value === this.value) return;
    this.value = value;
    for (const listener of this.#listeners) listener(value);
  }
  subscribe(listener: (status: T) => void): () => void {
    this.#listeners.add(listener);
    listener(this.value);
    return () => this.#listeners.delete(listener);
  }
}

/** Ignore late acknowledgements; callers decide what a timeout means for their operation. */
export function acknowledgeWithin<T>(
  send: (ack: (value: T) => void) => void,
  timeoutMs: number,
): Promise<T | undefined> {
  return new Promise((resolve, reject) => {
    let settled = false;
    const timer = window.setTimeout(() => {
      settled = true;
      resolve(undefined);
    }, timeoutMs);
    try {
      send((value) => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timer);
        resolve(value);
      });
    } catch (error) {
      settled = true;
      window.clearTimeout(timer);
      reject(error instanceof Error ? error : new Error("Socket request failed", { cause: error }));
    }
  });
}

export class SyncRequest {
  #pending: Promise<void> | undefined;
  #generation = 0;

  invalidate(): void {
    this.#generation += 1;
    this.#pending = undefined;
  }

  run(request: (isCurrent: () => boolean) => Promise<void>): Promise<void> {
    const generation = this.#generation;
    this.#pending ??= request(() => generation === this.#generation).finally(() => {
      if (generation === this.#generation) this.#pending = undefined;
    });
    return this.#pending;
  }
}
